import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Recycle, Plus, Pencil, Trash2, ChevronDown, ChevronRight, Search, ShoppingBag, RotateCcw, Printer, Camera, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VHU_STATUTS = {
  en_demontage: { label: "En démontage", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
  demonte: { label: "Démonté", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  traite: { label: "Traité", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};
const PART_STATUTS = {
  disponible: { label: "Disponible", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  reserve: { label: "Réservé", color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  vendu: { label: "Vendu", color: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
};
const EMPTY_VHU = { marque: "", modele: "", immatriculation: "", vin: "", annee: "", date_entree: "", notes: "", statut: "en_demontage" };
const EMPTY_PART = { vhu_id: "", designation: "", reference_constructeur: "", emplacement: "", quantite: 1, prix_vente: 0, statut: "disponible", acheteur: "", prix_vente_final: 0, date_vente: "", notes: "", photo: "", photo_ct: "", modeles_compatibles: "" };

const plate = (t) => <span className="font-mono font-bold tracking-widest text-yellow-400 bg-slate-900 px-2 py-0.5 rounded border border-yellow-500/30 uppercase text-xs">{t}</span>;

export default function VHU() {
  const { isFull } = useAuth();
  const [vhus, setVhus] = useState([]);
  const [parts, setParts] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");

  const [vOpen, setVOpen] = useState(false);
  const [vEdit, setVEdit] = useState(null);
  const [vForm, setVForm] = useState(EMPTY_VHU);

  const [pOpen, setPOpen] = useState(false);
  const [pEdit, setPEdit] = useState(null);
  const [pForm, setPForm] = useState(EMPTY_PART);

  const [sellId, setSellId] = useState(null);
  const [sell, setSell] = useState({ acheteur: "", prix: 0 });
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    const [v, p, s] = await Promise.all([api.get("/vhu"), api.get("/vhu-parts"), api.get("/vhu/stats")]);
    setVhus(v.data); setParts(p.data); setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const partsOf = (id) => parts.filter((p) => p.vhu_id === id);

  const openNewVHU = () => { setVEdit(null); setVForm(EMPTY_VHU); setVOpen(true); };
  const openEditVHU = (v) => { setVEdit(v.id); setVForm({ ...EMPTY_VHU, ...v }); setVOpen(true); };
  const saveVHU = async () => {
    try {
      if (vEdit) await api.put(`/vhu/${vEdit}`, vForm); else await api.post("/vhu", vForm);
      toast.success("VHU enregistré"); setVOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delVHU = async (id) => { if (!window.confirm("Supprimer ce VHU et toutes ses pièces ?")) return; await api.delete(`/vhu/${id}`); toast.success("VHU supprimé"); load(); };

  const openNewPart = (vhuId) => { setPEdit(null); setPForm({ ...EMPTY_PART, vhu_id: vhuId }); setPOpen(true); };
  const openEditPart = (p) => { setPEdit(p.id); setPForm({ ...EMPTY_PART, ...p }); setPOpen(true); };
  const savePart = async () => {
    const payload = { ...pForm, quantite: Number(pForm.quantite) || 1, prix_vente: Number(pForm.prix_vente) || 0, prix_vente_final: Number(pForm.prix_vente_final) || 0 };
    try {
      if (pEdit) await api.put(`/vhu-parts/${pEdit}`, payload); else await api.post("/vhu-parts", payload);
      toast.success("Pièce enregistrée"); setPOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delPart = async (id) => { if (!window.confirm("Supprimer cette pièce ?")) return; await api.delete(`/vhu-parts/${id}`); toast.success("Pièce supprimée"); load(); };

  const openSell = (p) => { setSellId(p.id); setSell({ acheteur: "", prix: p.prix_vente || 0 }); };
  const doSell = async () => { await api.patch(`/vhu-parts/${sellId}/sell`, { acheteur: sell.acheteur, prix: Number(sell.prix) || 0 }); toast.success("Pièce vendue"); setSellId(null); load(); };
  const restore = async (id) => { await api.patch(`/vhu-parts/${id}/restore`); toast.success("Pièce remise disponible"); load(); };

  const printLabel = async (p) => {
    try {
      const res = await api.get(`/vhu-parts/${p.id}/label`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success("Étiquette générée");
    } catch (e) { toast.error("Erreur lors de la génération de l'étiquette"); }
  };

  const uploadPhoto = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vhu-parts/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo enregistrée");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur upload photo"); }
  };

  const delPhoto = async (id, idx) => {
    try { await api.delete(`/vhu-parts/${id}/photo/${idx}`); toast.success("Photo supprimée"); setLightbox(null); load(); }
    catch (e) { toast.error("Erreur suppression photo"); }
  };

  const results = q.trim() ? parts.filter((p) => `${p.designation} ${p.reference_constructeur} ${p.modeles_compatibles || ""}`.toLowerCase().includes(q.trim().toLowerCase())) : [];

  const PartRow = ({ p }) => {
    const st = PART_STATUTS[p.statut] || PART_STATUTS.disponible;
    return (
      <tr data-testid={`vhu-part-${p.id}`} className="hover:bg-[#1E2A3E]">
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {p.photos && p.photos.length > 0
                ? p.photos.slice(0, 3).map((_, idx) => (
                    <img key={idx} src={`${API}/vhu-parts/${p.id}/photo/${idx}`} alt="" onClick={() => setLightbox({ url: `${API}/vhu-parts/${p.id}/photo/${idx}`, id: p.id, idx })} className="w-10 h-10 rounded object-cover border border-[#2A364F] cursor-pointer hover:ring-2 hover:ring-cyan-400 shrink-0" />
                  ))
                : <div className="w-10 h-10 rounded bg-[#0A0D14] border border-[#2A364F] flex items-center justify-center text-[#64748B] shrink-0"><ImageIcon className="w-4 h-4" /></div>}
              {p.photos && p.photos.length > 3 && <div className="w-10 h-10 rounded bg-[#0A0D14] border border-[#2A364F] flex items-center justify-center text-[10px] text-[#94A3B8] shrink-0">+{p.photos.length - 3}</div>}
            </div>
            <div>
              <div className="text-[#F8FAFC]">{p.designation}</div>
              {p.modeles_compatibles && <div className="text-[10px] text-cyan-400/90">↔ {p.modeles_compatibles}</div>}
            </div>
          </div>
        </td>
        <td className="px-3 py-2.5"><span className="font-mono text-xs text-cyan-400">{p.reference_constructeur || "—"}</span></td>
        <td className="px-3 py-2.5 text-[#94A3B8] text-xs">{p.emplacement || "—"}</td>
        <td className="px-3 py-2.5 text-center font-mono text-[#F8FAFC]">{p.quantite}</td>
        <td className="px-3 py-2.5 text-right font-mono text-[#F59E0B]">{p.statut === "vendu" ? `${Number(p.prix_vente_final).toFixed(2)} €` : (p.prix_vente ? `${Number(p.prix_vente).toFixed(2)} €` : "—")}</td>
        <td className="px-3 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded border ${st.color}`}>{st.label}</span>{p.statut === "vendu" && p.acheteur && <div className="text-[10px] text-[#64748B] mt-0.5">{p.acheteur}</div>}</td>
        <td className="px-3 py-2.5">
          <div className="flex items-center justify-end gap-1">
            {p.statut !== "vendu"
              ? <Button data-testid={`sell-part-${p.id}`} onClick={() => openSell(p)} size="sm" className="h-7 bg-[#10B981] hover:bg-[#059669] text-white text-xs"><ShoppingBag className="w-3.5 h-3.5 mr-1" />Vendre</Button>
              : <Button data-testid={`restore-part-${p.id}`} onClick={() => restore(p.id)} size="sm" variant="outline" className="h-7 border-[#2A364F] bg-transparent text-[#94A3B8] text-xs"><RotateCcw className="w-3.5 h-3.5 mr-1" />Annuler</Button>}
            <label data-testid={`photo-part-${p.id}`} title="Photo" className="w-7 h-7 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-cyan-400 flex items-center justify-center cursor-pointer">
              <Camera className="w-3.5 h-3.5" />
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) uploadPhoto(p.id, e.target.files[0]); e.target.value = ""; }} />
            </label>
            <button data-testid={`label-part-${p.id}`} onClick={() => printLabel(p)} title="Étiquette" className="w-7 h-7 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#F59E0B] flex items-center justify-center"><Printer className="w-3.5 h-3.5" /></button>
            <button data-testid={`edit-part-${p.id}`} onClick={() => openEditPart(p)} className="w-7 h-7 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#3B82F6] flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
            <button data-testid={`delete-part-${p.id}`} onClick={() => delPart(p.id)} className="w-7 h-7 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#EF4444] flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="vhu-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC] flex items-center gap-2">
            <Recycle className="w-7 h-7 text-emerald-400" /> Pièces VHU
          </h1>
          <p className="text-[#94A3B8] text-sm">Véhicules hors d'usage — suivi des pièces, du stock et des ventes par véhicule et par référence constructeur.</p>
        </div>
        <Button data-testid="add-vhu-button" onClick={openNewVHU} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0D14] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Nouveau VHU
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          {[
            { l: "VHU", v: stats.nb_vhu, c: "text-[#F8FAFC]" },
            { l: "Pièces totales", v: stats.total_parts, c: "text-[#F8FAFC]" },
            { l: "Disponibles", v: stats.disponibles, c: "text-emerald-400" },
            { l: "Vendues", v: stats.vendues, c: "text-[#94A3B8]" },
            { l: "CA ventes", v: `${Number(stats.ca).toFixed(2)} €`, c: "text-[#F59E0B]" },
          ].map((s) => (
            <div key={s.l} className="bg-[#182232] border border-[#2A364F] rounded-xl p-4">
              <div className={`font-heading text-2xl font-extrabold ${s.c}`}>{s.v}</div>
              <div className="text-xs text-[#94A3B8] mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="vhu-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par référence constructeur ou désignation…" className="pl-9 bg-[#182232] border-[#2A364F]" />
      </div>

      {q.trim() ? (
        <div className="bg-[#182232] border border-[#2A364F] rounded-xl overflow-x-auto" data-testid="vhu-search-results">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-[#64748B] text-xs uppercase tracking-wider border-b border-[#2A364F]">
                <th className="text-left px-3 py-3">Désignation</th>
                <th className="text-left px-3 py-3">Réf. constructeur</th>
                <th className="text-left px-3 py-3">Véhicule VHU</th>
                <th className="text-center px-3 py-3">Qté</th>
                <th className="text-right px-3 py-3">Prix</th>
                <th className="text-left px-3 py-3">Statut</th>
                <th className="text-right px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A364F]">
              {results.map((p) => {
                const st = PART_STATUTS[p.statut] || PART_STATUTS.disponible;
                return (
                  <tr key={p.id} data-testid={`vhu-result-${p.id}`} className="hover:bg-[#1E2A3E]">
                    <td className="px-3 py-2.5 text-[#F8FAFC]">{p.designation}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-cyan-400">{p.reference_constructeur || "—"}</td>
                    <td className="px-3 py-2.5 text-xs">{p.vhu ? <span className="text-[#94A3B8]">{p.vhu.marque} {p.vhu.modele} {p.vhu.immatriculation && `· ${p.vhu.immatriculation}`}</span> : "—"}</td>
                    <td className="px-3 py-2.5 text-center font-mono text-[#F8FAFC]">{p.quantite}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[#F59E0B]">{p.statut === "vendu" ? `${Number(p.prix_vente_final).toFixed(2)} €` : (p.prix_vente ? `${Number(p.prix_vente).toFixed(2)} €` : "—")}</td>
                    <td className="px-3 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded border ${st.color}`}>{st.label}</span></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {p.statut !== "vendu"
                          ? <Button onClick={() => openSell(p)} size="sm" className="h-7 bg-[#10B981] hover:bg-[#059669] text-white text-xs"><ShoppingBag className="w-3.5 h-3.5 mr-1" />Vendre</Button>
                          : <Button onClick={() => restore(p.id)} size="sm" variant="outline" className="h-7 border-[#2A364F] bg-transparent text-[#94A3B8] text-xs"><RotateCcw className="w-3.5 h-3.5 mr-1" />Annuler</Button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {results.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#64748B]">Aucune pièce trouvée pour « {q} ».</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-3">
          {vhus.map((v) => {
            const vp = partsOf(v.id);
            const st = VHU_STATUTS[v.statut] || VHU_STATUTS.en_demontage;
            const isOpen = expanded === v.id;
            return (
              <div key={v.id} data-testid={`vhu-card-${v.id}`} className="bg-[#182232] border border-[#2A364F] rounded-xl overflow-hidden">
                <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <button onClick={() => setExpanded(isOpen ? null : v.id)} className="flex items-center gap-3 text-left min-w-0">
                    {isOpen ? <ChevronDown className="w-5 h-5 text-[#64748B] shrink-0" /> : <ChevronRight className="w-5 h-5 text-[#64748B] shrink-0" />}
                    <Recycle className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-[#F8FAFC] flex items-center gap-2 flex-wrap">
                        {v.marque} {v.modele} {v.immatriculation && plate(v.immatriculation)}
                      </div>
                      <div className="text-xs text-[#64748B]">{v.annee && `${v.annee} · `}{v.vin && `VIN ${v.vin} · `}{vp.length} pièce(s)</div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] px-2.5 py-1 rounded border ${st.color}`}>{st.label}</span>
                    <button data-testid={`edit-vhu-${v.id}`} onClick={() => openEditVHU(v)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#3B82F6] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                    {isFull && <button data-testid={`delete-vhu-${v.id}`} onClick={() => delVHU(v.id)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#EF4444] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-[#2A364F] p-4 bg-[#0A0D14]/40">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold text-[#F8FAFC]">Pièces démontées</div>
                      <Button data-testid={`add-part-${v.id}`} onClick={() => openNewPart(v.id)} size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"><Plus className="w-4 h-4 mr-1" />Ajouter une pièce</Button>
                    </div>
                    {vp.length === 0 ? (
                      <div className="text-sm text-[#64748B] py-4 text-center border border-dashed border-[#2A364F] rounded-lg">Aucune pièce enregistrée pour ce véhicule.</div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-[#2A364F]">
                        <table className="w-full text-sm min-w-[760px]">
                          <thead>
                            <tr className="text-[#64748B] text-xs uppercase tracking-wider border-b border-[#2A364F] bg-[#182232]">
                              <th className="text-left px-3 py-2.5">Désignation</th>
                              <th className="text-left px-3 py-2.5">Réf. constructeur</th>
                              <th className="text-left px-3 py-2.5">Emplacement</th>
                              <th className="text-center px-3 py-2.5">Qté</th>
                              <th className="text-right px-3 py-2.5">Prix</th>
                              <th className="text-left px-3 py-2.5">Statut</th>
                              <th className="text-right px-3 py-2.5">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#2A364F]">
                            {vp.map((p) => <PartRow key={p.id} p={p} />)}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {vhus.length === 0 && (
            <div className="bg-[#182232] border border-[#2A364F] rounded-xl p-10 text-center">
              <Recycle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <div className="text-[#F8FAFC] font-semibold">Aucun VHU enregistré</div>
              <div className="text-sm text-[#64748B] mt-1">Ajoutez un véhicule hors d'usage pour suivre ses pièces.</div>
            </div>
          )}
        </div>
      )}

      {/* VHU dialog */}
      <Dialog open={vOpen} onOpenChange={setVOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{vEdit ? "Modifier le VHU" : "Nouveau VHU"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[#94A3B8]">Marque *</Label><Input data-testid="vhu-marque" value={vForm.marque} onChange={(e) => setVForm({ ...vForm, marque: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Modèle</Label><Input value={vForm.modele} onChange={(e) => setVForm({ ...vForm, modele: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Immatriculation</Label><Input data-testid="vhu-immat" value={vForm.immatriculation} onChange={(e) => setVForm({ ...vForm, immatriculation: e.target.value.toUpperCase() })} className="mt-1 bg-[#182232] border-[#2A364F] font-mono" /></div>
            <div><Label className="text-[#94A3B8]">VIN</Label><Input value={vForm.vin} onChange={(e) => setVForm({ ...vForm, vin: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Année</Label><Input value={vForm.annee} onChange={(e) => setVForm({ ...vForm, annee: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Date d'entrée</Label><Input type="date" value={vForm.date_entree} onChange={(e) => setVForm({ ...vForm, date_entree: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div className="col-span-2">
              <Label className="text-[#94A3B8]">Statut</Label>
              <Select value={vForm.statut} onValueChange={(v) => setVForm({ ...vForm, statut: v })}>
                <SelectTrigger data-testid="vhu-statut" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(VHU_STATUTS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-[#94A3B8]">Notes</Label><Textarea value={vForm.notes} onChange={(e) => setVForm({ ...vForm, notes: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-vhu-button" onClick={saveVHU} disabled={!vForm.marque} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Part dialog */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{pEdit ? "Modifier la pièce" : "Nouvelle pièce démontée"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-[#94A3B8]">Désignation *</Label><Input data-testid="part-designation" value={pForm.designation} onChange={(e) => setPForm({ ...pForm, designation: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" placeholder="Ex. Porte avant droite" /></div>
            <div><Label className="text-[#94A3B8]">Référence constructeur</Label><Input data-testid="part-ref" value={pForm.reference_constructeur} onChange={(e) => setPForm({ ...pForm, reference_constructeur: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F] font-mono" placeholder="Ex. 8200123456" /></div>
            <div><Label className="text-[#94A3B8]">Emplacement</Label><Input value={pForm.emplacement} onChange={(e) => setPForm({ ...pForm, emplacement: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" placeholder="Rayon / palette" /></div>
            <div className="col-span-2"><Label className="text-[#94A3B8]">Modèles compatibles</Label><Input data-testid="part-modeles" value={pForm.modeles_compatibles} onChange={(e) => setPForm({ ...pForm, modeles_compatibles: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" placeholder="Ex. Clio III, Modus, Kangoo II" /><p className="text-[10px] text-[#64748B] mt-1">Séparez par des virgules — permet de retrouver la pièce par recherche véhicule.</p></div>
            <div><Label className="text-[#94A3B8]">Quantité</Label><Input type="number" min={1} value={pForm.quantite} onChange={(e) => setPForm({ ...pForm, quantite: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Prix de vente (€)</Label><Input type="number" step="0.01" value={pForm.prix_vente} onChange={(e) => setPForm({ ...pForm, prix_vente: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div className="col-span-2">
              <Label className="text-[#94A3B8]">Statut</Label>
              <Select value={pForm.statut} onValueChange={(v) => setPForm({ ...pForm, statut: v })}>
                <SelectTrigger data-testid="part-statut" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PART_STATUTS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-[#94A3B8]">Notes</Label><Textarea value={pForm.notes} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-part-button" onClick={savePart} disabled={!pForm.designation} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sell dialog */}
      <Dialog open={!!sellId} onOpenChange={(o) => { if (!o) setSellId(null); }}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Vendre la pièce</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[#94A3B8]">Acheteur</Label><Input data-testid="sell-acheteur" value={sell.acheteur} onChange={(e) => setSell({ ...sell, acheteur: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" placeholder="Nom du client / atelier" /></div>
            <div><Label className="text-[#94A3B8]">Prix de vente (€)</Label><Input data-testid="sell-prix" type="number" step="0.01" value={sell.prix} onChange={(e) => setSell({ ...sell, prix: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSellId(null)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-sell-button" onClick={doSell} className="bg-[#10B981] hover:bg-[#059669] text-white">Confirmer la vente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Lightbox photo */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 sm:p-8" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <img src={lightbox.url} alt="" className="max-h-[85vh] max-w-[92vw] rounded-lg border border-[#2A364F]" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2"><X className="w-5 h-5" /></button>
          <button data-testid="lightbox-delete" onClick={(e) => { e.stopPropagation(); delPhoto(lightbox.id, lightbox.idx); }} className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#EF4444] hover:bg-[#DC2626] text-white text-sm px-4 py-2 rounded-lg flex items-center gap-2"><Trash2 className="w-4 h-4" /> Supprimer cette photo</button>
        </div>
      )}
    </div>
  );
}
